import { Button, DropdownSelect } from '@components/common';
import React, { useEffect, useMemo, useState } from 'react';
import {
  ASSIGNABLE_POLICY_ROLES,
  AccessPolicySummary,
} from '../../../types/accessControl';
import { Outlet } from '../../../types/outlet';

interface RoleAssignmentSectionProps {
  outlet: Outlet;
  policies: AccessPolicySummary[];
  assignedPolicyIds: Record<string, string | null>;
  savingRoleKey: string | null;
  onSave: (
    outletId: string,
    role: (typeof ASSIGNABLE_POLICY_ROLES)[number],
    policyId: string | null,
  ) => Promise<void>;
}

const formatRoleLabel = (value: string) =>
  value
    .split('_')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');

export const RoleAssignmentSection: React.FC<RoleAssignmentSectionProps> = ({
  outlet,
  policies,
  assignedPolicyIds,
  savingRoleKey,
  onSave,
}) => {
  const [draftSelections, setDraftSelections] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    setDraftSelections(
      Object.fromEntries(
        ASSIGNABLE_POLICY_ROLES.map((role) => [
          role,
          assignedPolicyIds[role] || '',
        ]),
      ),
    );
  }, [assignedPolicyIds]);

  const policyOptions = useMemo(
    () =>
      policies.map((policy) => ({
        value: policy.id,
        label: policy.name,
      })),
    [policies],
  );

  return (
    <div className="rounded-2xl border border-gray-200 bg-white">
      <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
        <div>
          <h3 className="text-base font-semibold text-black">{outlet.name}</h3>
          <p className="text-xs text-gray-600">
            Attach one policy per role for this outlet.
          </p>
        </div>
        <span className="rounded-full bg-app-bg px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-600">
          {Object.values(assignedPolicyIds).filter(Boolean).length} assigned
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-app-bg/50 text-left text-[11px] font-semibold uppercase tracking-[0.24em] text-gray-500">
            <tr>
              <th className="px-5 py-3">Role</th>
              <th className="px-5 py-3">Policy</th>
              <th className="px-5 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {ASSIGNABLE_POLICY_ROLES.map((role) => {
              const savingKey = `${outlet.id}:${role}`;
              const selectedPolicyId = draftSelections[role] || '';

              return (
                <tr key={role} className="border-t border-gray-100">
                  <td className="px-5 py-4">
                    <div>
                      <p className="font-semibold text-black">
                        {formatRoleLabel(role)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {assignedPolicyIds[role]
                          ? 'Policy currently assigned'
                          : 'No policy assigned'}
                      </p>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <DropdownSelect
                      value={selectedPolicyId}
                      onChange={(v) =>
                        setDraftSelections((current) => ({
                          ...current,
                          [role]: v,
                        }))
                      }
                      options={[
                        { value: '', label: 'No policy' },
                        ...policyOptions,
                      ]}
                      searchable={policyOptions.length > 5}
                    />
                  </td>
                  <td className="px-5 py-4 text-right">
                    <Button
                      size="sm"
                      isLoading={savingRoleKey === savingKey}
                      onClick={() =>
                        onSave(outlet.id, role, selectedPolicyId || null)
                      }
                    >
                      Save
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
